import { type BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

type CustomAttributes = { error?: Error | string } & Record<string, unknown>;
type LogLevel = 'DEBUG' | 'ERROR' | 'INFO' | 'TRACE' | 'WARN';

type NewRelicFeatures = {
  generic_events: {
    featAggregate: {
      harvestScheduler: {
        runHarvest: (props: { unload?: boolean }) => void;
      };
    };
  };
};

class NewRelicClient {
  private instance: BrowserAgent | null = null;

  addPageAction(name: string, attributes?: Record<string, unknown>) {
    this.instance?.addPageAction(name, attributes);
  }

  flush() {
    if (!this.instance) return;

    const scheduler = (this.instance.features as NewRelicFeatures)?.generic_events?.featAggregate
      ?.harvestScheduler;
    if (!scheduler) return;

    scheduler.runHarvest({});
  }

  getInteraction() {
    return this.instance?.interaction();
  }

  isInitialized() {
    return !!this.instance;
  }

  log(message: string, options?: { customAttributes?: CustomAttributes; level?: LogLevel }) {
    this.instance?.log(message, options);
  }

  noticeError(message: string, customAttributes?: CustomAttributes) {
    let error = customAttributes?.error;
    if (error instanceof Error) {
      if (!error.message) error.message = message;
      else error.message = `${message} (${error.message})`;
      delete customAttributes?.error;
    } else {
      error = new Error(message);
    }

    this.instance?.noticeError(error, customAttributes);
  }

  setCustomAttribute(name: string, value: boolean | null | number | string, persist = false) {
    this.instance?.setCustomAttribute(name, value, persist);
  }

  setInstance(instance: BrowserAgent) {
    this.instance = instance;
    this.instance.setApplicationVersion(APP_VERSION);
  }

  setUserId(userId: null | string) {
    this.instance?.setUserId(userId);
  }
}

export const newRelic = new NewRelicClient();
